import { Container, Loader, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { OrganizationSetup } from "../components/OrganizationSetup/OrganizationSetup";
import {
	BetaAccessStatus,
	useGetCurrentUserAndOrganizationsQuery,
} from "../graphql/generated/types.gen";
import { useAuthCompat } from "../hooks/useAuthCompat";
import { useIsMobile } from "../hooks/useIsMobile";

export const Route = createFileRoute("/organization-setup")({
	component: OrganizationSetupPage,
});

function OrganizationSetupPage() {
	const { user, isLoading, signIn } = useAuthCompat();
	const isMobile = useIsMobile();
	const { data: userData } = useGetCurrentUserAndOrganizationsQuery({
		skip: !user,
	});

	const currentUser = userData?.currentUser;
	const organizations = userData?.organizations;
	const isWaitlisted =
		currentUser?.betaAccessStatus === BetaAccessStatus.Waitlist;

	// Handle redirects based on authentication and user state
	useEffect(() => {
		if (isLoading) return;

		// Redirect to login if not authenticated — via signIn, so it takes the
		// direct-to-WorkOS path instead of the bounce-prone /v1/auth redirect
		if (!user) {
			signIn(window.location.pathname + window.location.search);
			return;
		}

		// Redirect to waitlist if user is on the waitlist
		if (currentUser && isWaitlisted) {
			window.location.href = "/waitlist";
			return;
		}

		// Redirect to main app if user already has organizations
		if (organizations?.length) {
			window.location.href = "/";
		}
	}, [user, currentUser, organizations, isLoading, isWaitlisted, signIn]);

	// Show loading while determining auth state
	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	// Not authenticated - show loading while redirect happens
	if (!user) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	// Waitlisted - show loading while redirect happens
	if (isWaitlisted) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	// Has organizations - show loading while redirect happens
	if (organizations?.length) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	// Still loading user data from GraphQL
	if (!currentUser) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	const subtitleText = "Create your organization to get started.";

	return (
		<div
			className="min-h-screen absolute inset-0 z-50 overflow-hidden"
			style={{ background: "var(--canvas)" }}
		>
			{/* Scrollable content */}
			<div
				className="relative z-10 h-full overflow-y-auto"
				style={{ paddingTop: isMobile ? 52 : 80 }}
			>
				<Container size="md" py="xl" px="md">
					<div className="flex flex-col items-center text-center py-8 gap-6">
						<Title order={1} size="2.5rem" fw={700}>
							Welcome to Relatance
						</Title>

						<p className="text-lg text-gray-400 max-w-[600px] mx-auto">
							{subtitleText}
						</p>
					</div>

					<OrganizationSetup />
				</Container>
			</div>
		</div>
	);
}
